import React, { FC } from 'react';
import { useRouter } from 'next/router';
import { gql, useQuery } from '@apollo/client';
import { DialogOverlay, DialogContent } from '@reach/dialog';

import AppLink from '../../AppLink';
import CloseIcon from '../../Icons/Close';
import SearchIcon from '../../Icons/Search';
import { buildCardsUrl } from '../../../utils/urls';
import fastpanel from '../../../utils/fastpanel';
import colors from '../../../utils/colorConstants';

import useModalToggleTracking from '../../../hooks/useModalToggleTracking';
import useFirstInput from '../../../hooks/useFirstInput';
import ConstructorAutocompleteInput from '@/components/ConstructorAutocompleteInput';

export interface Props {
  className?: string;
  searchId?: string;
  startingValue?: string;
  isOpen: boolean;
  onDismiss: () => void;
}

const FEATURED_CATEGORIES_QUERY = gql`
  query getFeaturedCategories {
    featuredNavNodes {
      color
      customUrl
      desktopDefaultCrossSellingTab
      desktopFlyerCategories
      id
      mobileDefaultCrossSellingTab
      mobileFlyerCategories
      slug
      title: fullTitle
      type
    }
  }
`;

export const MobileSearchModal: FC<Props> = (props: Props) => {
  const { isOpen, onDismiss, startingValue } = props;
  useModalToggleTracking(isOpen, { id: 'search' });

  const router = useRouter();
  const { query } = router;
  const { q = '' } = query;

  const { inputValue, onInputChange } = useFirstInput(startingValue || q);

  const searchSubmit = (event: any) => {
    event.preventDefault();

    const query = event.target?.[0]?.value || '';

    fastpanel({
      name: 'pbr_search_requested',
      data: {
        entry_point: 'all',
        searched_for: query,
      },
    });

    const href = query ? `/search?q=${query}` : `/cards`;
    router.push(href);
    dismiss();
  };

  const dismiss = () => {
    onDismiss();
  };

  const { data = {}, error, loading } = useQuery(FEATURED_CATEGORIES_QUERY);

  if (error || loading) {
    return null;
  }

  const listItems = data.featuredNavNodes.map((data: any) => {
    const { slug, title, type } = data;
    return (
      <li key={slug}>
        <AppLink
          href={buildCardsUrl(`${type}/${slug}`)}
          onClick={dismiss}
          tracking={{
            id: 'search_all_categories',
          }}
        >
          {title}
        </AppLink>
      </li>
    );
  });

  return (
    <DialogOverlay
      className="fixed inset-0 overflow-auto bg-lapel bg-opacity-20 z-50"
      isOpen={isOpen}
      onDismiss={dismiss}
    >
      <DialogContent aria-label="Search Modal" className="bg-white w-full">
        <div className="flex flex-row justify-between py-2 items-center border-b border-dove">
          <span aria-hidden="true" className="px-3">
            <SearchIcon fill={colors.charcoal} height="1.5rem" width="1.5rem" />
          </span>
          <form action="/search" className="w-full" method="get" onSubmit={searchSubmit}>
            <div className="relative flex items-center">
              <ConstructorAutocompleteInput
                autoCapitalize="none"
                className="border-0 paragraph-medium h-9 px-2 rounded-full text-lapel placeholder-charcoal border-alto bg-gallery"
                name="q"
                onChange={onInputChange}
                placeholder="Search in all categories"
                type="text"
                value={inputValue}
              />
            </div>
          </form>
          <span aria-hidden="true" className="px-4" onClick={dismiss}>
            <CloseIcon height="2rem" width="2rem" />
          </span>
        </div>
        <div className="flex flex-col h-full overflow-y-scroll paragraph-medium p-4">
          <div className="text-wolf mb-4 font-bold">Top categories</div>
          <ul className="space-y-4">{listItems}</ul>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};

export default React.memo(MobileSearchModal);
